import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { BookOpen, TrendingUp, AlertCircle, Loader2, Link2, Database } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * KB Analytics — admin/unlimited-only panel. Shows which knowledge-base
 * entries Claude is actually referencing, what % of replies get KB-grounded,
 * and which user questions got NO match (= gap list = what to index next).
 */
export default function KBAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);

  const load = useCallback(async (d = days) => {
    setLoading(true); setError(null);
    try {
      const token = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
      const res = await fetch(`${API_URL}/api/kb/analytics?days=${d}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      console.error('[KBAnalytics] fetch failed:', e);
      setError(e.message);
    }
    setLoading(false);
  }, [days]);

  useEffect(() => { load(days); }, [days, load]);

  if (loading && !data) {
    return (
      <Card className="bg-[#0A0A0A] border-zinc-800">
        <CardContent className="p-6 flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />Loading KB analytics…
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card className="bg-red-500/5 border-red-500/30">
        <CardContent className="p-4 text-xs text-red-300">KB Analytics error: {error}</CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const rate = (data.kb_grounding_rate * 100).toFixed(1);

  return (
    <Card className="bg-[#0A0A0A] border-zinc-800" data-testid="kb-analytics-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white font-['Outfit'] text-base">
          <BookOpen className="w-5 h-5 text-cyan-400" />
          Knowledge Base Analytics
          <Badge className="bg-cyan-500/10 text-cyan-300 text-[9px] ml-2 border border-cyan-500/20">ADMIN</Badge>
          <div className="ml-auto flex gap-1">
            {[1, 7, 30].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`kb-analytics-window-${d}d`}
                className={`text-[10px] px-2 py-0.5 rounded-sm border transition-colors ${
                  days === d
                    ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-300'
                    : 'border-zinc-700 text-zinc-500 hover:border-zinc-600'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </CardTitle>
        <CardDescription className="text-zinc-500 text-xs">
          Which external tools Claude is recommending — and which questions got no match (= gap list).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary stats */}
        <div className="grid grid-cols-4 gap-2">
          <Stat label="AI replies" value={data.total_assistant_messages} />
          <Stat label="KB-grounded" value={data.kb_grounded_messages} color="cyan" />
          <Stat label="Grounding rate" value={`${rate}%`}
                color={data.kb_grounding_rate > 0.15 ? 'emerald' : data.kb_grounding_rate > 0.05 ? 'amber' : 'zinc'} />
          <Stat label="KB entries" value={data.kb_entry_count} color="zinc" />
        </div>

        {/* Top referenced resources */}
        <div>
          <div className="flex items-center gap-1 mb-2">
            <TrendingUp className="w-3 h-3 text-emerald-400" />
            <h3 className="text-[11px] uppercase tracking-wider text-emerald-400 font-bold">Top Referenced</h3>
          </div>
          {data.top_resources.length === 0 ? (
            <p className="text-[11px] text-zinc-500 italic">
              No KB references in this window. Either the KB didn't match any questions, or no one asked.
            </p>
          ) : (
            <div className="space-y-1">
              {data.top_resources.map((r) => {
                const maxCount = data.top_resources[0].count;
                const pct = (r.count / maxCount) * 100;
                return (
                  <div
                    key={r.id}
                    data-testid={`kb-top-${r.id}`}
                    className="relative p-2 bg-[#050505] border border-zinc-800 rounded-sm overflow-hidden"
                  >
                    <div
                      className="absolute inset-y-0 left-0 bg-cyan-500/10"
                      style={{ width: `${pct}%` }}
                    />
                    <div className="relative flex items-center justify-between gap-2">
                      <div className="min-w-0 flex items-center gap-2 flex-1">
                        <span className="text-xs font-bold text-white font-['JetBrains_Mono'] w-8 flex-shrink-0">
                          {r.count}×
                        </span>
                        <span className="text-xs text-zinc-300 truncate">{r.title}</span>
                        <Badge className="bg-zinc-800 text-zinc-400 text-[9px] px-1.5 py-0 border-0">
                          {r.category}
                        </Badge>
                      </div>
                      {r.url && !r.url.startsWith('internal://') && (
                        <a
                          href={r.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-zinc-500 hover:text-cyan-400 flex-shrink-0"
                          title={r.url}
                        >
                          <Link2 className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Gap list */}
        {data.unmatched_samples.length > 0 && (
          <div>
            <div className="flex items-center gap-1 mb-2">
              <AlertCircle className="w-3 h-3 text-amber-400" />
              <h3 className="text-[11px] uppercase tracking-wider text-amber-400 font-bold">
                Gap List — questions the KB didn't cover
              </h3>
              <Badge className="bg-amber-500/10 text-amber-300 text-[9px] border border-amber-500/20">
                {data.unmatched_samples.length}
              </Badge>
            </div>
            <p className="text-[10px] text-zinc-500 mb-2">
              If you see a recurring theme below, add a new entry to <code className="text-cyan-300">tuning_resources.json</code>.
            </p>
            <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
              {data.unmatched_samples.map((s, i) => (
                <div
                  key={`${i}-${s.slice(0, 20)}`}
                  className="p-2 bg-amber-500/5 border border-amber-500/15 rounded-sm text-[10px] text-amber-200/90 leading-relaxed"
                >
                  {s}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="pt-2 border-t border-zinc-800 flex items-center gap-2 text-[9px] text-zinc-600 font-['JetBrains_Mono']">
          <Database className="w-2.5 h-2.5" />
          source: db.chat_history · window {data.window_days}d · rolling
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, color = 'white' }) {
  const colorMap = {
    white: 'text-white',
    cyan: 'text-cyan-300',
    emerald: 'text-emerald-300',
    amber: 'text-amber-300',
    zinc: 'text-zinc-400',
  };
  return (
    <div className="p-2 bg-[#050505] border border-zinc-800 rounded-sm">
      <p className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`text-lg font-bold font-['JetBrains_Mono'] ${colorMap[color]}`}>{value}</p>
    </div>
  );
}
